import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import mammoth from "mammoth";

import useAxios from "../utils/useAxios";
import { useSSO } from "./AuthContext";

const ProposalInputContext = createContext();

export default ProposalInputContext;

export const ProposalInputProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const { apiRes, setApiRes } = useSSO();

  const initValues = {
    opportunityName: '',
    opportunityCrmRefNo: '',
    date: dayjs(Date.now()),
    accountName: '',
    uniqueCompanyId: '',
    companyAddress: '',
    customerName: '',
    customerJobTitle: '',
    customerEmailAddress: '',
    customerPhoneNumber: '',
    bdmFullName: '',
    bdmEmail: '',
    bdmPhone: '',
    quoteWriterJobTitle: '',
    market: '',
    opportunityDetails: '',
    numWorkPackages: '',
    overallPrice: '',
    projectDuration: '',
    companyUrl: '',
    opportunityObjectives: '',
    assumptions: '',
    outOfScopeItems: '',
    meetingMinutes: null,
    inputFile: null,
    otherFiles: [],
    randomness: 0.3,
  };
  const [values, setValues] = useState(() => {
    const stored = localStorage.getItem("proposalInputData");
    if (stored) {
      const parsed = JSON.parse(stored);

      return {
        ...parsed,
        date: parsed.date ? dayjs(parsed.date) : null,
      };
    }
    return initValues;
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const collapseObj = {
    showFileUpload: true,
    showProposalInfo: false,
    showCompanyInfo: false,
    showCustomerInfo: false,
    showBDMInfo: false,
    showOpportunityInfo: false,
    showDocumentInfo: false,
  };
  const expandObj = {
    showProposalInfo: true,
    showCompanyInfo: true,
    showCustomerInfo: true,
    showBDMInfo: true,
    showOpportunityInfo: true,
    showDocumentInfo: true,
  };
  const [collapseValues, setCollapseValue] = useState(collapseObj);
  const [file, setFile] = useState(null);

  const randomnessArray = [0, 0.3, 0.5, 0.7, 1];
  const marketAreaArray = ['HealthTech', 'Pharma', 'Materials', 'Other'];

  useEffect(() => {
    setValues(prev => ({
      ...prev,
      date: dayjs(Date.now()),
    }));
  }, []);

  const fieldMap = {
    "Proposal Title": "opportunityName",
    "Proposal Reference": "opportunityCrmRefNo",
    "Company Name": "accountName",
    "Registered Company Number": "uniqueCompanyId",
    "Company Address": "companyAddress",
    "Customer Full Name": "customerName",
    "Customer Job Title": "customerJobTitle",
    "Customer Email Address": "customerEmailAddress",
    "Customer main phone number": "customerPhoneNumber",
    "BDM Full Name": "bdmFullName",
    "BDM Email Address": "bdmEmail",
    "BDM Main Phone Number": "bdmPhone",
    "Job Title of Quote Writer": "quoteWriterJobTitle",
    "Market Area": "market",
    "Opportunity Details": "opportunityDetails",
    "Number of work packages required": "numWorkPackages",
    "Initial estimate of project cost": "overallPrice",
    "Project Duration": "projectDuration",
    "Company Website URL": "companyUrl",
    "Objectives and goals": "opportunityObjectives",
    "Assumptions": "assumptions",
    "Out of scope items": "outOfScopeItems",
  };

  useEffect(() => {
    localStorage.setItem(
      "proposalInputData",
      JSON.stringify({
        ...values,
        date: values.date ? values.date.toISOString() : null,
        meetingMinutes: null,
        otherFiles: [],
      })
    );
  }, [values]);

  const handleMinutesUpload = async (files) => {
    const showError = (message) => {
      setFormErrors(prev => ({
        ...prev,
        meetingMinutes: message,
      }));
    };

    if (!files?.length) {
      return showError("Please upload a valid file.");
    }

    const file = files[0];
    const fileType = file.name.split(".").pop()?.toLowerCase();

    if (!["txt", "docx"].includes(fileType)) {
      return showError("Please upload a DOCX or TXT file.");
    }

    if (fileType === "docx") {
      const arrayBuffer = await file.arrayBuffer();
      const { value: html } = await mammoth.convertToHtml({ arrayBuffer });

      const doc = new DOMParser().parseFromString(html, "text/html");

      if (doc.querySelector("table")) {
        return showError("Tables are not allowed in meeting minutes.");
      }
    }

    setValues((prev) => ({
      ...prev,
      meetingMinutes: file,
    }));
  };

  const handleOtherFileUpload = (files) => {
    if (files.length > 0) {
      const file = files[0];
      setValues(prev => ({
        ...prev,
        otherFiles: [...prev.otherFiles, file],
      }));
    } else {
      setFormErrors(prev => ({
        ...prev,
        otherFiles: 'Please upload a valid file.',
      }));
    }
  };

  const handleMinuteRemove = () => {
    setValues(prev => ({
      ...prev,
      meetingMinutes: null,
    }));
  };

  const handleOtherFileRemove = (fileName) => {
    setValues(prev => ({
      ...prev,
      otherFiles: prev.otherFiles.filter(file => file.name !== fileName),
    }));
  };

  const handleClear = () => {
    setIsSubmit(false);
    setFile(null);
    setValues(initValues);
    setFormErrors({});
  };

  const validate = () => {
    const errors = {};

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^(?:\+?[1-9]\d{6,14}|0\d{9,14})$/;
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+([a-z]{2,})(\/[^\s]*)?$/i;

    if (!values?.opportunityName) {
      errors['opportunityName'] = 'Proposal title is required!';
    }
    if (!values?.opportunityCrmRefNo) {
      errors['opportunityCrmRefNo'] = 'Proposal reference is required!';
    }
    if (!values?.date) {
      errors['date'] = 'Date is required!';
    }
    if (!values?.accountName) {
      errors['accountName'] = 'Company name is required!';
    }
    if (!values?.uniqueCompanyId) {
      errors['uniqueCompanyId'] = 'Registered company number is required!';
    }
    if (!values?.companyAddress) {
      errors['companyAddress'] = 'Company address is required!';
    }
    if (!values?.customerName) {
      errors['customerName'] = 'Customer full name is required!';
    }
    if (!values?.customerJobTitle) {
      errors['customerJobTitle'] = 'Customer job title is required!';
    }
    if (!values?.customerEmailAddress) {
      errors['customerEmailAddress'] = 'Customer email address is required!';
    } else if (!emailRegex.test(values?.customerEmailAddress)) {
      errors['customerEmailAddress'] = 'This is not a valid email format!';
    }
    if (!values?.customerPhoneNumber) {
      errors['customerPhoneNumber'] = 'Customer main phone number is required!';
    } else if (!phoneRegex.test(values?.customerPhoneNumber)) {
      errors['customerPhoneNumber'] = 'This is not a valid phone number format!';
    }
    if (!values?.bdmFullName) {
      errors['bdmFullName'] = 'BDM full name is required!';
    }
    if (!values?.bdmEmail) {
      errors['bdmEmail'] = 'BDM email address is required!';
    } else if (!emailRegex.test(values?.bdmEmail)) {
      errors['bdmEmail'] = 'This is not a valid email format!';
    }
    if (!values?.bdmPhone) {
      errors['bdmPhone'] = 'BDM main phone number is required!';
    } else if (!phoneRegex.test(values?.bdmPhone)) {
      errors['bdmPhone'] = 'This is not a valid phone number format!';
    }
    if (!values?.quoteWriterJobTitle) {
      errors['quoteWriterJobTitle'] = 'Job title of quote writer is required!';
    }
    if (!values?.market) {
      errors['market'] = 'Market area is required!';
    }
    if (!values?.opportunityDetails) {
      errors['opportunityDetails'] = 'Opportunity details is required!';
    }
    if (!values?.numWorkPackages) {
      errors['numWorkPackages'] = 'Number of work packages is required!';
    } else if (values?.numWorkPackages > 10) {
      errors['numWorkPackages'] = 'Number of work packages cannot exceed 10!';
    } else if (values?.numWorkPackages < 1) {
      errors['numWorkPackages'] = 'Number of work packages must be at least 1!';
    }
    if (!values?.overallPrice) {
      errors['overallPrice'] = 'Initial estimate of project cost is required!';
    } else if (values?.overallPrice < 1) {
      errors['overallPrice'] = 'Initial estimate of project cost must be at least 1!';
    }
    if (!values?.projectDuration) {
      errors['projectDuration'] = 'Estimate duration in months is required!';
    } else if (values?.projectDuration < 1) {
      errors['projectDuration'] = 'Estimated duration must be at least 1 month!';
    }
    if (!values?.companyUrl) {
      errors['companyUrl'] = 'Company website-url is required!';
    } else if (!urlRegex.test(values?.companyUrl)) {
      errors['companyUrl'] = 'Company website-url is not valid format!';
    }
    if (!values?.meetingMinutes) {
      errors['meetingMinutes'] = 'Meeting minutes is required!';
    }
    if (values?.meetingMinutes?.name === values?.inputFile?.name) {
      errors['meetingMinutes'] = 'Meeting minutes file must be different from the input file!';
    }
    if (!values?.randomness) {
      errors['randomness'] = 'Randomness is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      generateProposal();
    } else if (Object.keys(formErrors).length !== 0 && isSubmit) {
      setCollapseValue(expandObj);
    }
  }, [formErrors]);

  const generateProposal = async () => {
    setApiRes({ ...apiRes, loading: true });

    try {
      const data = {
        opportunity_name: values.opportunityName,
        opportunity_crm_ref_no: values.opportunityCrmRefNo,
        date: values.date,
        account_name: values.accountName,
        unique_company_id: values.uniqueCompanyId,
        company_address: values.companyAddress,
        customer_name: values.customerName,
        customer_job_title: values.customerJobTitle,
        customer_email_address: values.customerEmailAddress,
        customer_phone_number: values.customerPhoneNumber,
        bdm_full_name: values.bdmFullName,
        bdm_email: values.bdmEmail,
        bdm_phone: values.bdmPhone,
        quote_writer_job_title: values.quoteWriterJobTitle,
        market: values.market,
        opportunity_details: values.opportunityDetails,
        num_work_packages: values.numWorkPackages,
        overall_price: values.overallPrice,
        project_duration: values.projectDuration,
        company_url: `https://${values.companyUrl}`,
        opportunity_objectives: values.opportunityObjectives,
        assumptions: values.assumptions,
        out_of_scope: values.outOfScopeItems,
        randomness: values.randomness,
      };

      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      formData.append("meeting_summary", values.meetingMinutes);

      values?.otherFiles?.forEach((file) => {
        formData.append("other_files", file);
      });

      const response = await api.post(`/api/generate-proposal`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setApiRes({ ...apiRes, loading: false });
      navigate(`/proposal/${response.data}`);

    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '')
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Generate Proposal',
      });
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setValues(prev => ({
        ...prev,
        inputFile: file,
      }));

      const arrayBuffer = await file.arrayBuffer();

      const result = await mammoth.convertToHtml({ arrayBuffer });
      const html = result.value;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const tables = doc.querySelectorAll("table");

      if (!tables.length) return;

      const newValues = {};

      Array.from(tables).forEach((table) => {
        const rows = table.querySelectorAll("tr");

        rows.forEach((tr) => {
          const cells = Array.from(tr.querySelectorAll("td, th")).map(cell => {
            let html = cell.innerHTML;

            // Convert <br> tags to newlines
            html = html.replace(/<br\s*\/?>/gi, "\n");

            // Convert paragraph endings to newlines
            html = html.replace(/<\/p>/gi, "\n");

            // Remove remaining HTML tags
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;

            return tempDiv.textContent.trim();
          });

          if (cells.length < 2) return;

          const key = cells[0].replace(/\s+/g, " ").trim();
          let value = cells[1];

          if (key === "Market Area") {
            if (!marketAreaArray.includes(value)) value = '';
          } else if (key === "Company Website URL") {
            value = value
              .replace(/^(https?:\/\/)?(www\.)?/i, "")
              .replace(/\/$/, "");
          } else if (key === "Project Duration") {
            value = Math.round(value / 4);
          }

          const mappedField = fieldMap[key];

          if (mappedField) newValues[mappedField] = value;
        });
      });

      setValues(prev => ({
        ...prev,
        ...newValues
      }));
    } catch (err) {
      setApiRes(prev => ({
        ...prev,
        axiosError: true,
        errHeading: 'Word file processing',
        errMsg: err,
      }));
    }
  };

  useEffect(() => {
    if (file) {
      handleFileUpload(file);

      setCollapseValue(expandObj);
    }
  }, [file]);

  const sectionStyle = {
    bgcolor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 3,
    width: '100%',
    p: 2,
    '&:hover': {
      boxShadow: 3,
    },
  };

  const contextData = {
    values: values,
    setValues: setValues,
    formErrors: formErrors,
    handleMinutesUpload: handleMinutesUpload,
    handleOtherFileUpload: handleOtherFileUpload,
    handleMinuteRemove: handleMinuteRemove,
    handleOtherFileRemove: handleOtherFileRemove,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    handleClear: handleClear,
    collapseValues: collapseValues,
    setCollapseValue: setCollapseValue,
    randomnessArray: randomnessArray,
    marketAreaArray: marketAreaArray,
    file: file,
    setFile: setFile,
    sectionStyle: sectionStyle,
  };

  return (
    <ProposalInputContext.Provider value={contextData}>
      {children}
    </ProposalInputContext.Provider>
  );
};